import { useState } from "react";
import { Link } from "react-router-dom";
import type { Hotel } from "@/api/types";
import { useWorkspace } from "@/app/workspace";

function BankIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M3 9h18M5 9v9m4-9v9m6-9v9m4-9v9M3 18h18M12 3 3 7v2h18V7l-9-4Z" />
    </svg>
  );
}

function CardIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <rect x="3" y="5" width="18" height="14" rx="1.5" />
      <path d="M3 9h18M7 14h4" />
    </svg>
  );
}

function OtaIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M17.5 13.5A7.5 7.5 0 1 0 13 18M3 10.5h15M10.5 3a11.5 11.5 0 0 1 0 15M10.5 3a11.5 11.5 0 0 0 0 15" />
      <path d="M21 17.5c0 2.25-3 4.5-3 4.5s-3-2.25-3-4.5a3 3 0 0 1 6 0Z" />
      <circle cx="18" cy="17.5" r=".75" />
    </svg>
  );
}

const modules = [
  {
    path: "/bancos",
    icon: BankIcon,
    label: "Bancos",
    description: "Extractos y Libro Mayor",
  },
  {
    path: "/tarjetas",
    icon: CardIcon,
    label: "Tarjetas",
    description: "Liquidaciones y acreditaciones",
  },
  {
    path: "/cobranzas-ota",
    icon: OtaIcon,
    label: "OTAs",
    description: "Reservas y cobranzas",
  },
] as const;

function HotelIcon({ hotelName }: { hotelName: string }) {
  return (
    <span
      className="hotel-card__logo-fallback"
      role="img"
      aria-label={`Ícono de hotel de ${hotelName}`}
    >
      <svg viewBox="0 0 96 96" aria-hidden="true" focusable="false">
        <path d="M20 78V24h56v54M12 78h72M32 36h8m16 0h8M32 50h8m16 0h8M32 64h8m16 0h8M44 78V62h8v16" />
        <path d="M16 24h64" />
      </svg>
    </span>
  );
}

function HotelLogo({ hotel }: { hotel: Hotel }) {
  const [failed, setFailed] = useState(false);

  if (!hotel.logo || failed) return <HotelIcon hotelName={hotel.name} />;

  return (
    <img
      src={`/${hotel.logo}`}
      alt={`Logo de ${hotel.name}`}
      onError={() => setFailed(true)}
    />
  );
}

export function HomePage() {
  const { hotels, setHotelId, isLoading, error, retry } = useWorkspace();

  return (
    <div className="hotel-home">
      <header className="hotel-home__lead">
        <span className="eyebrow">Conciliaciones 525</span>
        <h1>Elegí el hotel</h1>
        <p>
          {"Seleccioná un hotel para conciliar movimientos bancarios, liquidaciones de tarjetas y cobranzas de OTAs."}
        </p>
      </header>
      {isLoading && (
        <div className="empty-state">
          <p>Conectando con la API…</p>
        </div>
      )}
      {error && (
        <section className="empty-state" role="alert">
          <h2>No pudimos conectar con la API</h2>
          <p>El backend no está disponible o no responde.</p>
          <button className="button button--primary" onClick={retry}>
            Reintentar conexión
          </button>
        </section>
      )}
      <section className="hotel-cards">
        {hotels.map((hotel) => (
          <article className="hotel-card" key={hotel.id}>
            <div className="hotel-card__identity">
              <HotelLogo hotel={hotel} />
              <div>
                <small>Hotel</small>
                <h2>{hotel.name}</h2>
                <p>{hotel.legalName || "Operación hotelera 525"}</p>
              </div>
            </div>
            <nav aria-label={`Conciliaciones de ${hotel.name}`}>
              {modules.map((module) => {
                const ModuleIcon = module.icon;

                return (
                  <Link
                    key={module.path}
                    to={module.path}
                    onClick={() => setHotelId(hotel.id)}
                  >
                    <span className="hotel-card__module-icon">
                      <ModuleIcon />
                    </span>
                    <div>
                      <strong>{module.label}</strong>
                      <small>{module.description}</small>
                    </div>
                    <i aria-hidden="true">→</i>
                  </Link>
                );
              })}
            </nav>
          </article>
        ))}
      </section>
    </div>
  );
}
